import { Location } from 'react-router';
import { LoginResponseDto, CEntity, IEntity, IDataChart, TEntity, TMode, ICompSetting } from '@ts/ui-models';
import { IDataGridConfig } from '@ts/storybook';

export interface IRoute {
  id: string;
  matchPath: string;
  fullPath?: string; // if exist first navigate to this route if not then go to matchPath
  redirectPath?: string; //if someone put a wrong route in the url, it will redirect to this route
  pageComponent: any;
  pageTitle: string; //title refiled from IRouteState
  aclId: string; //empty string for public routes
  params?: object; //values from url selected for this route
  query?: object; //values from url selected for the query (filtering)
  queryString?: string;
  canFilter?: boolean; //add filter to this route from id
  routeLastBread?: string; //Link to navigate
  //spec?: object;
}
export interface IRouteState {
  menuOption?: IMenuItem; //predominates before the title of the route
  title?: string; //if we need a custom title in some pages
  resetHistoryLevel?: number; // if 0 then reset all history
  replace?: boolean; //replace last position, return same history
  routeLastBread?: string; //link for navigate
  entity?: CEntity<IEntity>; //We send extra info if we want to see name and id in the last breadcrumb
}

export interface ICrudRoute {
  aclId: string;
  entityName: string;
  DataTable: any;
  FormPage: any | { edit?: any; new?: any; view?: any };
  customEntityName?: string | { table?: string; edit?: string; new?: string; view?: string; matchPath?: string };
  customId?: string | { table?: string; edit?: string; new?: string; view?: string };
  customTitle?: { table?: string; edit?: string; new?: string; view?: string };
  canFilter?: boolean; // add filter to this route from id only in dataGrid
}

export interface ITransferPropsRoute {
  id?: string;
  path?: string;
  query?: any;
  params?: any;
  location?: any;
}

export interface IAppProps {
  app: any;
  data: any;
  transferPropsRoute: ITransferPropsRoute;
}

export interface IRouterProps {
  transferPropsRoute: ITransferPropsRoute;
  navigate: (route: string | number, state?: any) => void; //WithRouter
  location: Location; //WithRouter
  mode: TMode;
}
export interface IMinAppProps {
  data: any;
}

export interface ISessionBadge {
  organizationId: string;
  roleId: string;
  userId: string;
  isAdmin: boolean;
  profileId: any;
}

export interface IAcl {
  create: boolean;
  decommission: boolean;
  delete: boolean;
  edit: boolean;
  read: boolean;
  share: boolean;
}

export interface IMenuItem {
  id: string; // this id it will be unic and is used for find dataGrid config
  component?: 'route' | 'divider' | 'label' | 'blank';
  name?: string; //label
  icon?: string; //in component route mandatory
  route?: string;
  enabled?: boolean;
}

export type TPredefinedFilter = Record<string, { itemType: string }>;

export enum ELoginState {
  LOGGED_OUT = 'LOGGED_OUT',
  LOGGING_IN = 'LOGGING_IN',
  LOADING_USER_PARAMS = 'LOADING_USER_PARAMS',
  LOGGED_IN = 'LOGGED_IN',
  //LOGGING_IN_AUTO = 'LOGGING_IN_AUTO',
  //CHANGING_ROLE = 'CHANGING_ROLE',
  //CHANGING_PROFILE = 'CHANGING_PROFILE',
  //LOADING_PARAMS = 'LOADING_PARAMS',
  //LOGGING_OUT = 'LOGGING_OUT',
}

export interface ILoginResponse extends LoginResponseDto {
  displayName: string;
  loginBadge: ISessionBadge;
  defaultImage: string; //Avatar
}

export interface ISettings {
  dataGridConfig?: {
    [key: string]: IDataGridConfig;
  };
  navigations: any;
  logo: {
    logoId: string;
    logoUrl: string;
    logoCaption: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  menu: {
    menu: IMenuItem[];
    parameterId: string;
    level: string;
    levelValue: {
      roleLogicalId: string;
      organizationLogicalId: string;
    };
  };
  homeScreen: {
    homeScreen: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  locationsMapDisplaySettings: {
    hierarchyItemRelation: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  downstreamSnapshotCreationTimeout: {
    downstreamSnapshotCreationTimeout: number;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  rowsPerPage: number;
  forceItemTypes: boolean;
  useCustomFields: boolean;
  statusIndicatorsDownstreamTransactionMode: {
    statusIndicatorsDownstreamTransactionMode: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  detectionInterfaceTypes: Array<{
    technologyId: string;
    interfaceTypeId: string;
  }>;
  unitSystem: {
    unitSystem: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  colourSet: {
    colourSet: {
      primary: string;
      secondary: string;
    };
    parameterId: string;
    level: string;
    levelValue: string;
  };
  componentSettings: {
    [key: string]: ICompSetting;
  };
  mobileAppSettings: {
    periods: string;
    interval: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  defaultLanguage: {
    defaultLanguage: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  maximumSeverity: {
    maximumSeverity: string;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  minimumSeverity: {
    minimumSeverity: number;
    parameterId: string;
    level: string;
    levelValue: string;
  };
  quuppaSitePlannerConfig: {
    host: string;
    port: number;
    importedEntities: {
      tags: {
        enabled: boolean;
        itemTypeId: string;
        deleteUnused: boolean;
      };
      locators: {
        enabled: boolean;
        itemTypeId: string;
        deleteUnused: boolean;
      };
      locations: {
        enabled: boolean;
        itemTypeId: string;
        deleteUnused: boolean;
      };
      tagGroups: {
        enabled: boolean;
        deleteUnused: boolean;
      };
      backgroundImages: {
        view: string;
        layer: string;
        enabled: boolean;
      };
    };
  };
  predefinedFilter: {
    predefinedFilter: TPredefinedFilter;
    parameterId: string;
    level: string;
    levelValue: {
      roleLogicalId: string;
      organizationLogicalId: string;
    };
  };
  defaultView: {
    defaultViewId: string;
    parameterId: string;
    level: string;
    levelValue: string;
    tile: 'satellite' | 'normal';
    transparency: number;
  };
  predefinedFilters: TPredefinedFilter;
}

//Refactor
export interface IUnits {
  distribute: number;
  contentLastModified: string;
  creatorId: string;
  updaterId: string;
  logicalId: string;
  id: string;
  aliveStart: string;
  aliveEnd: any;
  isAlive: boolean;
  _ver: number;
  notes: any;
  name: string;
  system: string;
  prefix: number;
  abbreviation: string;
  conversionToSIBasic: number;
  conversionType: string;
  isSIBasic: boolean;
  quantityId: string;
  params: any;
  ownerOrganizationId: any;
  createdAt: string;
  updatedAt: string;
  quantity: IQuantity;
}

export interface IQuantity {
  creatorId: string;
  updaterId: string;
  logicalId: string;
  id: string;
  aliveStart: string;
  aliveEnd: any;
  isAlive: boolean;
  _ver: number;
  notes: any;
  name: string;
  isBase: boolean;
  createdAt: string;
  updatedAt: string;
}

//Charts
type getDataType = (() => Promise<any>) | ((slot: string) => Promise<any>);

export interface ListChart {
  id: number;
  title: string;
  slot?: string;
  data: IDataChart | undefined;
  getData: getDataType;
  loading: boolean;
}
